<template>
  <div class="__ChartDataTable">
    <div class="__ChartDataTable-DeselectBtn">
      <CButton
        color="primary"
        icon="close"
        flat
        size="sm"
        v-show="filterItems.length"
        @click="onClickDeselect"
      >
        {{ $t('Deselect') }}
      </CButton>
    </div>

    <q-table
      ref="table"
      :data="currentData"
      :columns="columns"
      :row-key="rowKey"
      :filter="searchQuery"
      :pagination.sync="pagination"
      :rows-per-page-options="rowsPerPageOptions"
      flat
      :loading="loading"
    >
      <template v-slot:header-cell-#total="props">
        <q-th :props="props">
          {{ props.col.label }}

          <!--<CPopupInfo>-->
          <!--  {{ $t('All {category} used during the period.', { category: categoryLabel }) }}-->
          <!--</CPopupInfo>-->
        </q-th>
      </template>

      <template v-slot:top-row="props">
        <q-tr :props="props" no-hover>
          <q-td v-for="val of props.cols" :key="val.name" class="__ChartDataTable-TopRowTd">
            <div v-if="val.name === 'date'">
              <div class="text-sm secondary-color">
                {{ $t('Total of period') }}
              </div>

              <div class="text-sm">
                {{ dateFormatter(dateRange.start) }} ~ <br />{{ dateFormatter(dateRange.end) }}
              </div>
            </div>

            <div class="text-right" v-else>
              {{ getTotalValue(val.name) }}
            </div>
          </q-td>
        </q-tr>
      </template>
    </q-table>
  </div>
</template>

<script>
import Vue from 'vue';
import moment from 'moment';
import { dateFormat } from '@/components/CustomCharts/chartFn';
import { formatNumber } from '@/lib/numbers';
import { sortObject } from '@/lib/sorter';
import CSearchBox from '@/components/uikit/CSearchBox';
import CButton from '@/components/uikit/CButton';
import CPopupInfo from '@/components/uikit/CPopupInfo';

const messages = {
  en: {
    Date: 'Date',
    Total: 'Total',
    'Total of period': 'Total of period',
    'All {category} used during the period.': 'All {category} used during the period.',
    Deselect: 'Unselect',
  },
  ja: {
    Date: '日付',
    Total: '全サービス',
    'Total of period': '期間の合計',
    'All {category} used during the period.': '期間中に利用したすべての{category}です。',
    Deselect: '選択を解除する',
  },
};

export default Vue.extend({
  name: 'ChartDataTable',
  i18n: { messages },
  components: { CSearchBox, CButton, CPopupInfo },
  props: {
    data: {
      type: Array,
      required: true,
      default: () => [],
    },
    series: {
      type: Array,
      required: true,
      default: () => [],
    },
    currentSeries: {
      type: Array,
      required: true,
      default: () => [],
    },
    filterItems: {
      type: Array,
      default: () => [],
    },
    showTotal: {
      type: Boolean,
    },
    dateRange: {
      type: Object,
      default: () => {},
      required: true,
    },
    currency: {
      type: String,
      required: true,
    },
    granularity: {
      type: String,
      required: true,
    },
    categoryLabel: {
      type: String,
      required: true,
    },
    totalLabel: {
      type: String,
      required: true,
    },
    locale: {
      type: String,
    },
    conditions: {
      type: Object,
      default: () => {},
    },
    rowKey: {
      type: String,
      default: 'id',
    },
    loading: {
      type: Boolean,
    },
  },
  data() {
    return {
      searchQuery: '',
      pagination: { rowsPerPage: 10 },
      rowsPerPageOptions: [10, 30, 50, 100, 0],
    };
  },
  watch: {
    'conditions.defaultTableRowsPerPage': function(val) {
      Vue.set(this.pagination, 'rowsPerPage', val);
    },
  },
  mounted() {
    this.$refs['table'].setPagination({ rowsPerPage: this.conditions.defaultTableRowsPerPage });
  },
  computed: {
    currentData() {
      if (!this.filterItems.length) return this.data;

      const filtered = [];

      this.filterItems.forEach(i => {
        let granularity;

        switch (this.granularity) {
          case 'daily':
            granularity = 'day';
            break;
          case 'monthly':
            granularity = 'month';
            break;
        }

        const target = this.data.find(d => moment(d.date).isSame(i, granularity));
        filtered.push(target);
      });

      return sortObject(filtered, 'date');
    },
    columns() {
      const date = {
        name: 'date',
        label: this.$t('Date'),
        style: 'width: 120px',
        align: 'left',
        field: 'date',
        format: val => this.dateFormatter(val),
        sortable: true,
      };

      const columns = this.currentSeries.map(s => {
        return {
          name: s.id,
          label: s.name === 'Total' ? this.totalLabel : s.name,
          align: 'right',
          field: s.id,
          format: val => formatNumber(val, this.currency),
        };
      });

      return [date, ...columns];
    },
  },
  methods: {
    getTotalValue(name) {
      const target = this.currentSeries.find(i => i.id === name);
      return target ? formatNumber(target.value, this.currency) : '';
    },

    onClickDeselect() {
      this.$emit('deselect');
    },

    dateFormatter(val) {
      const dateFormatStyle = this.granularity === 'monthly' ? 'YtoM' : 'L';

      return dateFormat(val, this.locale, dateFormatStyle);
    },
  },
});
</script>

<style lang="scss" scoped>
.__ChartDataTable {
  &-DeselectBtn {
    text-align: right;
    height: 30px;
  }

  &-TopRowTd {
    background-color: rgba(0, 0, 0, 0.03);
  }
}
</style>
